import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search,
  User,
  ArrowLeft,
  MessageSquare,
  RefreshCw,
  Send,
  Paperclip,
  X,
  FileText,
  MessageCircle,
  Phone,
  Clock,
  CheckCheck,
  Download,
  SmilePlus,
  Edit2,
  MoreVertical,
  Check,
  Upload,
  ChevronDown
} from 'lucide-react';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import { whatsappService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSocket } from '../../hooks/useSocket';
import { useSearchParams } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';

const emojis = ['👍', '❤️', '😂', '😮', '😢', '👏'];

// OPTIMIZATION: Moved static helper functions outside the component to prevent recreation on every render
const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Date.now() - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return formatDate(d);
};

const formatChatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const renderStatusIcon = (status) => {
  if (status === 'read') {
    return <CheckCheck className="w-3.5 h-3.5 text-blue-500 ml-0.5 flex-shrink-0" />;
  } else if (status === 'delivered') {
    return <CheckCheck className="w-3.5 h-3.5 text-gray-400 ml-0.5 flex-shrink-0" />;
  }
  return (
    <svg viewBox="0 0 11 9" width="12" height="12" className="text-gray-400 ml-0.5 flex-shrink-0">
      <path fill="currentColor" d="M3.243 6.643L1.076 4.476a.36.36 0 0 0-.51 0l-.36.36a.36.36 0 0 0 0 .51l2.784 2.783c.14.14.37.14.51 0L10.796 1.332a.36.36 0 0 0 0-.51l-.36-.36a.36.36 0 0 0-.51 0L3.243 6.643z" />
    </svg>
  );
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[1].length > 0) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.charAt(0).toUpperCase();
};

// eslint-disable-next-line no-unused-vars
export default function ConversationLogsView({ leads, currentUser }) {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultLeadId = searchParams.get('chatId');
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Edit & React States
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [showReactionMenuFor, setShowReactionMenuFor] = useState(null);
  const [showOptionsFor, setShowOptionsFor] = useState(null);

  const [toast, setToast] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const reactionMenuRef = useRef(null);
  const messageCache = useRef({});

  const emojis = ['👍', '❤️', '😂', '😮', '😢', '👏'];

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !attachedFile) || !selectedLead || sending) return;
    setSending(true);
    try {
      const media = attachedFile ? { base64: attachedFile.base64, mimeType: attachedFile.mimeType, name: attachedFile.name } : null;
      await whatsappService.sendMessage(selectedLead.phone, newMessage.trim(), selectedLead.id, media);
      setNewMessage('');
      setAttachedFile(null);
    } catch (error) {
      console.error('Error sending message:', error);
      setToast({ message: 'Failed to send: ' + error.message, type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleEditSubmit = async (messageId) => {
    if (!editMessageText.trim() || !selectedLead) {
      setEditingMessageId(null);
      return;
    }
    try {
      await whatsappService.editMessage(messageId, editMessageText.trim(), selectedLead.phone, selectedLead.id);
      setEditingMessageId(null);
      setEditMessageText('');
    } catch (error) {
      console.error('Error editing message:', error);
      setToast({ message: 'Failed to edit: ' + error.message, type: 'error' });
    }
  };

  const handleReact = async (messageId, emoji) => {
    try {
      await whatsappService.reactToMessage(messageId, emoji, selectedLead.phone, selectedLead.id);
      setShowReactionMenuFor(null);
    } catch (error) {
      console.error('Error reacting:', error);
      setToast({ message: 'Failed to react: ' + error.message, type: 'error' });
    }
  };

  // Click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reactionMenuRef.current && !reactionMenuRef.current.contains(event.target)) {
        setShowReactionMenuFor(null);
        setShowOptionsFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) {
      setToast({ message: 'File size must be under 16MB', type: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setAttachedFile({ base64, mimeType: file.type, name: file.name, preview: file.type.startsWith('image/') ? reader.result : null });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const employees = useMemo(() => {
    const unique = [...new Set(leads.map(l => l.manager).filter(Boolean))];
    return unique.sort();
  }, [leads]);

  const { socket } = useSocket();
  const [allConversations, setAllConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);

  useEffect(() => {
    if (defaultLeadId && allConversations.length > 0 && !selectedLead) {
      const match = allConversations.find(l => l.id == defaultLeadId);
      if (match) setSelectedLead(match);
    }
  }, [defaultLeadId, allConversations, selectedLead]);

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    if (lead) {
      // Push to history (not replace) so browser back button works on mobile
      setSearchParams({ chatId: lead.id }, { replace: false });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Watch for chatId being removed from URL (e.g. browser back button on mobile)
  useEffect(() => {
    const chatId = searchParams.get('chatId');
    if (!chatId && selectedLead) {
      setSelectedLead(null);
    }
  }, [searchParams]);

  const fetchConversations = async () => {
    try {
      const data = await whatsappService.getConversations();
      setAllConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  useEffect(() => { fetchConversations(); }, []);

  const leadsWithMessages = useMemo(() => {
    return allConversations.filter(l => {
      if (currentUser && currentUser.role !== 'Admin' && currentUser.role !== 'Owner') {
        if (l.manager !== currentUser.username && l.manager !== currentUser.name) return false;
      }
      if (filterEmployee !== 'all' && l.manager !== filterEmployee) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return l.clientName?.toLowerCase().includes(s) || l.phone?.includes(s) || l.manager?.toLowerCase().includes(s);
      }
      return true;
    });
  }, [allConversations, searchTerm, filterEmployee, currentUser]);

  const fetchMessages = async (leadId, showLoader = true) => {
    try {
      if (messageCache.current[leadId] && showLoader) {
        setMessages(messageCache.current[leadId]);
        showLoader = false;
      }

      if (showLoader) setLoading(true);
      const data = await whatsappService.getMessages(leadId);

      // OPTIMIZATION: Parse messages once upon fetch instead of on each render cycle
      const processedMessages = data.map(msg => {
        let parsedMsg = null;
        let isMedia = false;
        let textBody = msg.message;
        let isEdited = false;
        let clientReaction = null;
        let selfReaction = null;

        try {
          const parsed = JSON.parse(msg.message);
          parsedMsg = parsed;
          if (parsed.mediaType) {
            isMedia = true;
          } else if (parsed.text !== undefined) {
            textBody = parsed.text;
            isEdited = parsed.isEdited || false;
            clientReaction = parsed.reactionFromClient || null;
            selfReaction = parsed.reactionFromMe || null;
          }
        } catch (e) {
          // normal text
        }

        return {
          ...msg,
          parsedMsg,
          isMedia,
          textBody,
          isEdited,
          clientReaction,
          selfReaction
        };
      });
      setMessages(processedMessages);
      messageCache.current[leadId] = processedMessages;
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedLead) { setMessages([]); return; }
    fetchMessages(selectedLead.id);
  }, [selectedLead]);

  useEffect(() => {
    if (!socket) return;
    const handleMessageUpdate = (data) => {
      try {
        // Only play notification if new lead OR direction is 'inbound'
        if (data.direction === 'inbound') {
          const audio = new Audio('/notification.mp3');
          audio.play().catch(e => console.log('Audio play blocked by browser:', e));
        }
      } catch (err) { }

      fetchConversations();
      if (selectedLead && data.leadId === selectedLead.id) {
        fetchMessages(selectedLead.id, false);
      }
    };

    const handleChatRead = (data) => {
      fetchConversations();
    };

    socket.on('activity_added', handleMessageUpdate);
    socket.on('lead_created', handleMessageUpdate);
    socket.on('chat_read', handleChatRead);
    return () => {
      socket.off('activity_added', handleMessageUpdate);
      socket.off('lead_created', handleMessageUpdate);
      socket.off('chat_read', handleChatRead);
    };
  }, [socket, selectedLead]);

  return (
    <div className="h-[calc(100vh-64px)] -mx-4 sm:-mx-6 flex flex-col">

      {/* Page heading — matches LeadsView / CallListView */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 flex-shrink-0">
        <h2 className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
          {t('conversations') || 'Conversations'}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          {leadsWithMessages.length} {t('conversations') || 'conversations'}
          {leadsWithMessages.filter(l => l.hasUnreadMessages).length > 0 &&
            ` · ${leadsWithMessages.filter(l => l.hasUnreadMessages).length} unread`}
        </p>
      </div>

      {/* Main card container — rounded-xl style like rest of CRM */}
      <div className="flex-1 flex mx-0 sm:mx-6 mb-0 sm:mb-6 bg-white rounded-none sm:rounded-xl border-t sm:border border-gray-200 shadow-sm overflow-hidden">

        {/* ═══ Left Panel ═══ */}
        <div className={`${selectedLead ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-[340px] xl:w-[380px] border-r border-gray-200 bg-white flex-shrink-0`}>

          {/* Search & Filter */}
          <div className="p-3 border-b border-gray-200 bg-slate-50 space-y-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('searchConversations') || 'Search conversations...'}
                className="w-full pl-9 pr-3 h-[38px] text-sm bg-white border border-gray-200 rounded-lg text-gray-700 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {(currentUser?.role === 'Admin' || currentUser?.role === 'Owner') && (
              <Dropdown className="w-full">
                <DropdownTrigger>
                  <button
                    type="button"
                    className="w-full text-xs bg-white hover:bg-slate-50 border border-gray-200 rounded-lg px-3 h-[38px] text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center text-left shadow-sm transition-colors"
                  >
                    <span className="font-medium truncate">
                      {filterEmployee === 'all' ? (t('allEmployees') || 'All Employees') : filterEmployee}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-2" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                  <DropdownMenu
                    selectedKeys={new Set([filterEmployee])}
                    selectionMode="single"
                    onSelectionChange={(keys) => setFilterEmployee(Array.from(keys)[0])}
                  >
                    <DropdownItem id="all">{t('allEmployees') || 'All Employees'}</DropdownItem>
                    {employees.map((emp) => (
                      <DropdownItem key={emp} id={emp}>
                        {emp}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            )}
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {loadingConversations && allConversations.length === 0 ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex gap-3">
                    <Skeleton variant="circular" width={48} height={48} animation="wave" className="flex-shrink-0" />
                    <div className="flex-1 space-y-2 py-1">
                      <Skeleton width="50%" height={14} animation="wave" />
                      <Skeleton width="90%" height={12} animation="wave" />
                    </div>
                  </div>
                ))}
              </div>
            ) : leadsWithMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <MessageCircle className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm text-gray-400">{t('noConversationsFound') || 'No conversations found'}</p>
              </div>
            ) : (
              leadsWithMessages.map((lead) => {
                const isSelected = selectedLead?.id === lead.id;
                return (
                  <button
                    key={lead.id}
                    onClick={() => handleSelectLead(lead)}
                    className={`w-full flex items-center gap-3 px-3 py-3.5 text-left transition-all duration-150 border-b border-gray-100 ${isSelected
                        ? 'bg-blue-50 border-l-[3px] border-l-blue-600'
                        : 'hover:bg-gray-50 border-l-[3px] border-l-transparent'
                      }`}
                  >
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${lead.hasUnreadMessages
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-500'
                      }`}>
                      {getInitials(lead.clientName)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-0.5">
                        <span className={`truncate text-sm ${lead.hasUnreadMessages ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>
                          {lead.clientName}
                        </span>
                        <span className={`text-[10px] flex-shrink-0 ml-2 ${lead.hasUnreadMessages ? 'text-blue-600 font-bold' : 'text-gray-400'}`}>
                          {formatTimestamp(lead.lastMessageAt)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-[12px] text-gray-500 truncate flex items-center gap-1">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 flex-shrink-0">
                            {lead.manager || 'N/A'}
                          </span>
                          <span className="truncate">{lead.lastMessagePreview || 'Tap to view...'}</span>
                        </p>
                        {lead.hasUnreadMessages && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 ml-1"></span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ═══ Right Chat Pane ═══ */}
        <div className={`relative flex-col flex-1 bg-gray-50 ${selectedLead ? 'flex' : 'hidden lg:flex'}`}>

          {selectedLead ? (
            <div className="flex flex-col h-full">

              {/* ── Chat Header ── */}
              <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center gap-3 flex-shrink-0">
                <button onClick={() => handleSelectLead(null)} className="lg:hidden p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg mr-1">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {getInitials(selectedLead.clientName)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-gray-900 text-[15px] leading-5 truncate">{selectedLead.clientName}</h2>
                  <p className="text-[12px] text-gray-500 leading-4 truncate flex items-center gap-1.5">
                    <Phone className="w-3 h-3" /> {selectedLead.phone} · {selectedLead.manager || 'Unassigned'}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide flex-shrink-0
                    ${selectedLead.stage === 'Booked' ? 'bg-green-100 text-green-700' :
                    selectedLead.stage === 'New' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                  }`}>
                  {selectedLead.stage}
                </span>
              </div>

              {/* ── Messages Area ── */}
              <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 flex flex-col gap-0.5">
                {loading ? (
                  <div className="flex flex-col gap-4 p-4 h-full overflow-y-auto">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="space-y-4">
                        <div className="flex gap-3 max-w-[85%] mr-auto">
                          <Skeleton variant="circular" width={32} height={32} animation="wave" className="flex-shrink-0 mt-auto" />
                          <Skeleton variant="rounded" width={200} height={60} animation="wave" className="rounded-2xl rounded-bl-sm" />
                        </div>
                        <div className="flex gap-3 max-w-[85%] ml-auto justify-end">
                          <Skeleton variant="rounded" width={180} height={40} animation="wave" className="rounded-2xl rounded-br-sm bg-blue-100" />
                          <Skeleton variant="circular" width={32} height={32} animation="wave" className="flex-shrink-0 mt-auto" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-3">
                      <MessageSquare className="w-6 h-6 text-blue-400" />
                    </div>
                    <p className="text-sm text-gray-400">No messages yet. Send the first one!</p>
                  </div>
                ) : (
                  <>
                    {/* Date pill */}
                    <div className="flex justify-center mb-3">
                      <span className="bg-white border border-gray-200 text-gray-500 text-[11px] px-3 py-1 rounded-full shadow-sm font-medium">
                        {messages[0]?.timestamp
                          ? formatDate(messages[0].timestamp, { weekday: 'short', month: 'short', day: 'numeric' })
                          : 'Today'}
                      </span>
                    </div>

                    {messages.map((msg, index) => {
                      const isOutbound = msg.direction === 'outbound';
                      const isFirstInSequence = index === 0 || messages[index - 1].direction !== msg.direction;
                      const isLastInSequence = index === messages.length - 1 || messages[index + 1]?.direction !== msg.direction;

                      const { parsedMsg, isMedia, textBody, isEdited, clientReaction, selfReaction } = msg;

                      const hasReaction = clientReaction || selfReaction;
                      const baseMargin = isLastInSequence ? 'mb-2.5' : 'mb-[2px]';
                      const marginClass = hasReaction ? (isLastInSequence ? 'mb-[18px]' : 'mb-[14px]') : baseMargin;

                      return (
                        <div
                          key={msg.id}
                          onMouseEnter={() => setHoveredMessageId(msg.id)}
                          onMouseLeave={() => setHoveredMessageId(null)}
                          className={`flex ${marginClass} ${isOutbound ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`relative max-w-[75%] md:max-w-[65%] px-3 pt-1.5 pb-2 text-[14px] leading-[20px] break-words shadow-sm
                            ${isOutbound
                              ? `bg-blue-100 text-gray-800 rounded-2xl ${isFirstInSequence ? 'rounded-tr-md' : ''}`
                              : `bg-white border border-gray-200 text-gray-800 rounded-2xl ${isFirstInSequence ? 'rounded-tl-md' : ''}`
                            }`
                          }>

                            {/* Render Message Content */}
                            {isMedia ? (
                              <div className="flex flex-col gap-1.5 min-w-[150px] mb-1">
                                {/* Image Render */}
                                {parsedMsg.mediaType === 'image' && (
                                  <div
                                    className="rounded-lg overflow-hidden border border-black/5 bg-black/5 flex items-center justify-center cursor-pointer"
                                    onClick={() => setFullscreenImage({ src: `data:${parsedMsg.mimeType};base64,${parsedMsg.base64}`, filename: parsedMsg.filename || 'image.jpg' })}
                                  >
                                    <img
                                      src={`data:${parsedMsg.mimeType};base64,${parsedMsg.base64}`}
                                      alt={parsedMsg.caption || "Image"}
                                      className="max-h-[250px] w-auto object-contain hover:opacity-95 transition-opacity"
                                    />
                                  </div>
                                )}

                                {/* Audio Render */}
                                {parsedMsg.mediaType === 'audio' && (
                                  <audio controls className="w-full max-w-[280px] sm:max-w-[320px] h-[44px]" src={`data:${parsedMsg.mimeType};base64,${parsedMsg.base64}`} />
                                )}

                                {/* Video Render */}
                                {parsedMsg.mediaType === 'video' && (
                                  <div className="rounded-lg overflow-hidden border border-black/5 bg-black/5 flex items-center justify-center">
                                    <video
                                      controls
                                      className="max-h-[250px] w-auto max-w-full"
                                      src={`data:${parsedMsg.mimeType};base64,${parsedMsg.base64}`}
                                    />
                                  </div>
                                )}

                                {/* Document/File Render */}
                                {['document', 'sticker'].includes(parsedMsg.mediaType) && (
                                  <a
                                    href={`data:${parsedMsg.mimeType};base64,${parsedMsg.base64}`}
                                    download={parsedMsg.filename || `${parsedMsg.mediaType}-file`}
                                    className={`flex items-center gap-2 p-2.5 rounded-lg ${isOutbound ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-100 hover:bg-gray-200'} transition-colors cursor-pointer text-sm font-medium`}
                                  >
                                    <FileText className="w-5 h-5 flex-shrink-0" />
                                    <span className="truncate max-w-[160px]">{parsedMsg.filename || `Attached ${parsedMsg.mediaType}`}</span>
                                  </a>
                                )}

                                {/* Caption */}
                                {parsedMsg.caption && (
                                  <p className="mt-1 whitespace-pre-wrap">{parsedMsg.caption}</p>
                                )}
                                <span className="inline-block w-[60px] text-[0px]">&nbsp;</span>
                              </div>
                            ) : (
                              <>
                                {editingMessageId === msg.id ? (
                                  <div className="flex flex-col min-w-[200px]">
                                    <input
                                      autoFocus
                                      type="text"
                                      value={editMessageText}
                                      onChange={(e) => setEditMessageText(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleEditSubmit(msg.messageId); if (e.key === 'Escape') setEditingMessageId(null); }}
                                      className="w-full bg-white/50 border border-blue-300 rounded px-2 py-1 text-sm outline-none mb-1 text-gray-800"
                                    />
                                    <div className="flex justify-end gap-2">
                                      <button onClick={() => setEditingMessageId(null)} className="text-[10px] text-gray-500 hover:underline">Cancel</button>
                                      <button onClick={() => handleEditSubmit(msg.messageId)} className="text-[10px] text-blue-600 font-bold hover:underline">Save</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <span className="whitespace-pre-wrap">{textBody}</span>
                                    {/* The spacer below ensures text does not overlap with absolute positioned time/edited tags */}
                                    <span className={`inline-block ${isEdited ? 'w-[105px]' : 'w-[65px]'}`}>&nbsp;</span>
                                  </div>
                                )}

                                {/* Reactions Display */}
                                {(clientReaction || selfReaction) && (
                                  <div className={`absolute -bottom-4 left-2 bg-white border border-gray-200 shadow-sm rounded-full px-1.5 py-0.5 text-[12px] flex items-center gap-0.5 z-10`}>
                                    {clientReaction} {selfReaction}
                                  </div>
                                )}
                              </>
                            )}

                            {/* Timestamp & Edited status */}
                            <div className="absolute bottom-[5px] right-[10px] flex items-center gap-[3px]">
                              {isEdited && !editingMessageId && (
                                <span className="text-[10px] italic text-gray-400 mr-0.5 flex-shrink-0">Edited</span>
                              )}
                              <span className={`text-[10px] leading-none whitespace-nowrap ${isOutbound ? 'text-gray-500' : 'text-gray-400'}`}>
                                {formatChatTime(msg.timestamp)}
                              </span>
                              {isOutbound && renderStatusIcon(msg.status)}
                            </div>

                            {/* Hover Mechanics: Reaction & Edit Dropdown */}
                            {hoveredMessageId === msg.id && (
                              <div className={`absolute top-0 ${isOutbound ? '-left-12' : '-right-8'} h-full flex items-center px-1`} ref={reactionMenuRef}>

                                {/* React Button Context */}
                                <div className="relative">
                                  <button
                                    onClick={() => { setShowReactionMenuFor(msg.id); setShowOptionsFor(null); }}
                                    className="p-1.5 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 shadow-sm mx-0.5 transition-transform hover:scale-110"
                                  >
                                    <SmilePlus className="w-3.5 h-3.5" />
                                  </button>

                                  {/* Auto-opening horizontal reaction menu */}
                                  {showReactionMenuFor === msg.id && (
                                    <div className={`absolute top-1/2 -translate-y-1/2 ${isOutbound ? 'right-[120%]' : 'left-[120%]'} bg-white border border-gray-200 rounded-full shadow-md flex items-center p-1 z-20 gap-1`}>
                                      {emojis.map(emoji => (
                                        <button
                                          key={emoji}
                                          onClick={() => handleReact(msg.messageId, emoji)}
                                          className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-lg transition-transform hover:scale-125"
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* More Options Context (Edit Text) */}
                                {isOutbound && msg.type === 'text' && !msg.message.includes('mediaType') && (
                                  <div className="relative">
                                    <button
                                      onClick={() => { setShowOptionsFor(msg.id); setShowReactionMenuFor(null); }}
                                      className="p-1.5 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-gray-600 shadow-sm mx-0.5 transition-transform hover:scale-110"
                                    >
                                      <MoreVertical className="w-3.5 h-3.5" />
                                    </button>

                                    {showOptionsFor === msg.id && (
                                      <div className="absolute top-full lg:right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-md min-w-[120px] z-20 overflow-hidden py-1">
                                        <button
                                          onClick={() => {
                                            setEditingMessageId(msg.id);
                                            setEditMessageText(msg.textBody);
                                            setShowOptionsFor(null);
                                          }}
                                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 text-left"
                                        >
                                          <Edit2 className="w-4 h-4" /> Edit
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}

                              </div>
                            )}

                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* ── Attached File Preview ── */}
              {attachedFile && (
                <div className="bg-white border-t border-gray-200 px-4 py-2.5 flex items-center gap-3">
                  {attachedFile.preview ? (
                    <img src={attachedFile.preview} alt="Preview" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate font-medium">{attachedFile.name}</p>
                    <p className="text-xs text-gray-400">{attachedFile.mimeType}</p>
                  </div>
                  <button onClick={() => setAttachedFile(null)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              )}

              {/* ── Chat Input ── */}
              <div className="bg-white border-t border-gray-200 px-4 py-2.5 flex items-end gap-2 flex-shrink-0">
                <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={handleFileSelect} className="hidden" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex-shrink-0"
                  title="Attach file"
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                <div className="flex-1 bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 min-h-[40px] flex items-center focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                    }}
                    placeholder="Type a message..."
                    className="w-full bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none resize-none max-h-[100px] leading-5"
                    rows={1}
                    disabled={sending}
                  />
                </div>

                <button
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && !attachedFile) || sending}
                  className={`p-2.5 rounded-lg transition-all flex-shrink-0 ${(newMessage.trim() || attachedFile) && !sending
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  title="Send message"
                >
                  {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </div>

          ) : (
            /* ── Empty State ── */
            <div className="flex flex-col items-center justify-center h-full text-center px-10">
              <div className="max-w-xs">
                <div className="w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-blue-50">
                  <MessageCircle className="w-9 h-9 text-blue-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  {t('conversations') || 'Conversations'}
                </h2>
                <p className="text-sm text-gray-400 leading-5">
                  Select a conversation to start messaging.<br />All WhatsApp messages are securely logged.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4">
          <div className="absolute top-4 right-4 flex items-center gap-3 z-[110]">
            <a
              href={fullscreenImage.src}
              download={fullscreenImage.filename}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              title="Download Image"
            >
              <Download className="w-5 h-5" />
            </a>
            <button
              onClick={() => setFullscreenImage(null)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <img
            src={fullscreenImage.src}
            alt="Fullscreen preview"
            className="max-w-full max-h-[90vh] object-contain rounded-sm"
          />
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120]">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${toast.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
            }`}>
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 hover:opacity-80">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
